import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { maternityService } from '../services/maternityService'
import { Pregnancy, PregnancyFormData } from '../types/maternity'
import { calculateDueDate } from '../utils/dueDateCalculator'

// Helper to format date as YYYY-MM-DD
function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0]
}
import { formatDate } from '../utils/locale'
import './PregnancyForm.css'

interface PregnancyFormProps {
  patientId: string
  pregnancy?: Pregnancy
  onSuccess: () => void
  onCancel: () => void
}

export function PregnancyForm({ patientId, pregnancy, onSuccess, onCancel }: PregnancyFormProps) {
  const queryClient = useQueryClient()
  const [lastMenstrualPeriod, setLastMenstrualPeriod] = useState(
    pregnancy?.lastMenstrualPeriod || ''
  )
  const [notes, setNotes] = useState(pregnancy?.notes || '')
  const [estimatedDueDate, setEstimatedDueDate] = useState<string>('')

  useEffect(() => {
    if (lastMenstrualPeriod) {
      const edd = calculateDueDate(lastMenstrualPeriod)
      setEstimatedDueDate(formatDateForInput(edd))
    } else {
      setEstimatedDueDate('')
    }
  }, [lastMenstrualPeriod])

  const createMutation = useMutation({
    mutationFn: (data: PregnancyFormData) => maternityService.createPregnancy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pregnancies', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-summary', patientId] })
      onSuccess()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PregnancyFormData>) =>
      pregnancy
        ? maternityService.updatePregnancy(pregnancy.pregnancyId, patientId, data)
        : Promise.reject(new Error('Pregnancy not found')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pregnancies', patientId] })
      queryClient.invalidateQueries({ queryKey: ['pregnancy', pregnancy?.pregnancyId] })
      queryClient.invalidateQueries({ queryKey: ['patient-summary', patientId] })
      onSuccess()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!lastMenstrualPeriod) {
      alert('Last Menstrual Period (LMP) is required')
      return
    }

    const formData: PregnancyFormData = {
      patientId,
      lastMenstrualPeriod,
      notes: notes || undefined,
    }

    try {
      if (pregnancy) {
        await updateMutation.mutateAsync(formData)
      } else {
        await createMutation.mutateAsync(formData)
      }
    } catch (error) {
      console.error('Error saving pregnancy:', error)
      alert('Failed to save pregnancy. Please try again.')
    }
  }

  return (
    <form className="pregnancy-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="lmp">
          Last Menstrual Period (LMP) <span className="required">*</span>
        </label>
        <input
          id="lmp"
          type="date"
          value={lastMenstrualPeriod}
          onChange={(e) => setLastMenstrualPeriod(e.target.value)}
          required
          max={new Date().toISOString().split('T')[0]}
        />
      </div>

      {estimatedDueDate && (
        <div className="form-group">
          <label>Estimated Due Date (EDD)</label>
          <div className="edd-display">
            {formatDate(estimatedDueDate)}
            <span className="edd-note">(Calculated from LMP using Naegele's rule)</span>
          </div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="notes">Notes</label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Additional notes about this pregnancy..."
        />
      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={createMutation.isPending || updateMutation.isPending}
        >
          {createMutation.isPending || updateMutation.isPending
            ? 'Saving...'
            : pregnancy
              ? 'Update Pregnancy'
              : 'Create Pregnancy'}
        </button>
      </div>
    </form>
  )
}
